import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnInit,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BusinessRule, RuleTypes } from '@app/business-rules/model';
import { predicateDisplayString } from '@app/business-rules/utils';
import { OrgEndorsedService } from '@app/orgs/services/org-endorsed.service';
import { AuthService } from '@app/shared/services/auth.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { WindowToken } from '@app/shared/window.token';
import { SkillStandardsService } from '@app/skill-standards/services/skill-standards.service';
import { NotificationType } from '@lib/fresco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { SkillsStandardsApi } from '../../../skill-standards-api.model';

@Component({
  selector: 'dgx-add-existing-segments-to-skill-standards-modal',
  templateUrl:
    './add-existing-segments-to-skill-standards-modal.component.html',
  styleUrls: [
    './add-existing-segments-to-skill-standards-modal.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddExistingSegmentsToSkillStandardsModalComponent
  implements OnInit
{
  @Input() public existingSegments: BusinessRule[] = [];
  @Input() public pendingSegments?: BusinessRule[] = [];
  @Input() public allowManageSegments?: boolean = false;

  public readonly NotificationType = NotificationType;

  public skillStandard: SkillsStandardsApi.SkillStandardViewModel;
  public saving: boolean = false;
  public i18n: { [key: string]: string } = {};
  public endorsedSrc: string;
  public orgId: number;

  public readonly ruleType = RuleTypes.Segment;
  public readonly maxSegments = 12;

  constructor(
    private trackerService: TrackerService,
    private activeModal: NgbActiveModal,
    private translateService: TranslateService,
    private authService: AuthService,
    private orgEndorsedService: OrgEndorsedService,
    private skillStandardsService: SkillStandardsService,
    @Inject(WindowToken) private windowRef: Window,
    private sanitizer: DomSanitizer
  ) {}

  public get maximumSegmentsReached(): boolean {
    return (
      this.existingSegments.length + this.pendingSegments.length >=
      this.maxSegments
    );
  }

  public get existingAndPendingSegments(): BusinessRule[] {
    return [...this.existingSegments, ...this.pendingSegments];
  }

  public ngOnInit(): void {
    this.skillStandard = this.skillStandardsService.skillStandardsResponse;
    this.i18n = this.translateService.instant([
      'Core_Saving',
      'Core_Endorsed',
      'Core_Remove',
      'Core_Add',
      'OrgSkills_SearchForSkills',
      'TargetResourcesForm_Selected',
      'BusinessRules_SelectSegment',
      'BusinessRules_AddSegments',
      'BusinessRules_ManageSegments',
      'SkillStd_MaximumAddedSegmentsReached',
      'SkillStd_MaximumAddedSegmentsDesc',
    ]);
    this.orgId = this.authService.authUser?.defaultOrgInfo?.organizationId;
    this.endorsedSrc = this.orgEndorsedService.getEndorsedSrc(this.orgId);
  }
  public add(segment: BusinessRule): void {
    // Prevent duplicates
    if (
      this.pendingSegments.some(
        (ps) => ps.businessRuleId === segment.businessRuleId
      )
    ) {
      return;
    }

    this.pendingSegments = [...this.pendingSegments, segment];

    // TODO: figure out trackerService
    // Track user action
    // this.trackerService.trackEventData({
    //   action: 'Skill Adding Step Completed',
    //   properties: {
    //     SkillId:
    //       (tag as TagsSearchItem).id || (tag as Partial<TagsApi.Tag>).tagId,
    //     SkillName: tag.name,
    //   },
    // });
  }

  public remove(segment: BusinessRule) {
    this.pendingSegments = this.pendingSegments.filter(
      ({ businessRuleId }) => businessRuleId !== segment.businessRuleId
    );
  }

  public createNewSegment() {
    this.activeModal.close({
      pendingSegments: this.pendingSegments,
      createNewSegment: true,
    });
  }

  public save(): void {
    if (this.pendingSegments.length === 0) {
      return;
    }

    this.saving = true;

    this.skillStandardsService
      .addSkillStandardsSegments({
        organizationId: this.orgId,
        skillStandardId: this.skillStandard.skillStandardsId,
        segmentIds: this.pendingSegments.map(
          (segment) => segment.businessRuleId
        ),
      })
      .subscribe(() => {
        this.saving = false;
        this.activeModal.close({
          pendingSegments: this.pendingSegments,
          createNewSegment: false,
        });
      });
  }

  public trackById(index: number, item: any): string {
    return item.businessRuleId;
  }

  public displaySegmentNameAndAttributes(rule: BusinessRule): string {
    if (!rule) {
      return;
    }
    const ruleName = this.sanitizer.sanitize(
      SecurityContext.HTML,
      rule.ruleName
    );
    const attributes = this.sanitizer.sanitize(
      SecurityContext.HTML,
      predicateDisplayString(rule)
    );
    return `<div><b>${ruleName}</b></div><div class="color-ebony-a61">${attributes}</div>`;
  }

  public openManageSegmentsPage() {
    this.windowRef.open(`/orgs/${this.orgId}/members/segments`, '_blank');
  }
}
